<template>
  <div class="candidate-profile-detail">
    <b-row>
      <b-col cols="12" md="6" class="mb-5">
        <h5 class="font-weight-bold mb-3">Availability</h5>
        <p class="mb-1">
          <span class="font-weight-bold">Status:</span>
          {{ availabilityLabel }}
        </p>
        <p
          v-if="otw.employment_preference && otw.employment_preference.length"
          class="mb-1"
        >
          <span class="font-weight-bold">Employment Preference:</span>
          {{ otw.employment_preference.join(', ') }}
        </p>
        <p
          v-if="otw.preferred_locations && otw.preferred_locations.length"
          class="mb-1"
        >
          <span class="font-weight-bold">Preferred Locations:</span>
          {{ otw.preferred_locations.join(', ') }}
        </p>
      </b-col>

      <b-col cols="12" md="6" class="mb-5">
        <h5 class="font-weight-bold mb-3">FIFO Preferences</h5>
        <p class="mb-1">
          <span class="font-weight-bold">FIFO Open:</span>
          {{ otw.fifo_open ? 'Yes' : 'No' }}
        </p>
        <p v-if="otw.fifo_open && otw.roster_preference" class="mb-1">
          <span class="font-weight-bold">Roster Preference:</span>
          {{ otw.roster_preference }}
        </p>
      </b-col>
    </b-row>

    <b-row v-if="otw.about_me">
      <b-col cols="12" class="mb-5">
        <h5 class="font-weight-bold mb-2">About Me</h5>
        <p class="mb-0 candidate-profile-detail__about">{{ otw.about_me }}</p>
      </b-col>
    </b-row>

    <b-row v-if="hasSkills">
      <b-col cols="12" md="6" class="mb-5">
        <h5 class="font-weight-bold mb-3">Skills</h5>
        <div class="candidate-profile-detail__skills">
          <b-badge
            v-for="skill in otw.skills"
            :key="skill"
            pill
            class="candidate-profile-detail__skill-badge"
          >
            {{ skill }}
          </b-badge>
        </div>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <!-- Just the names of the files they hold, no status. -->
        <h5 class="font-weight-bold mb-3">Files</h5>
        <ul v-if="fileNames.length" class="candidate-profile-detail__files">
          <li v-for="(name, index) in fileNames" :key="index">{{ name }}</li>
        </ul>
        <p v-else class="text-muted mb-0">None listed</p>
      </b-col>
    </b-row>

    <!--
      D7: no employment history / qualifications sections — genuinely no
      source data anywhere in the codebase for Sprint 1. Skills / current
      state / relocation shipped later once CandidateSearchService::profile()
      started returning them.
    -->
  </div>
</template>

<script>
import dayjs from 'dayjs'

const AVAILABILITY_LABEL = {
  immediate: 'Available Immediately',
  next_week: 'Available Next Week',
  from_date: 'Available From',
}

export default {
  name: 'CandidateProfileDetail',

  props: {
    candidate: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    otw() {
      return (this.candidate && this.candidate.otw) || {}
    },
    fileNames() {
      return ((this.candidate && this.candidate.compliance_items) || [])
        .map((item) => item && item.name)
        .filter(Boolean)
    },
    hasSkills() {
      return Array.isArray(this.otw.skills) && this.otw.skills.length > 0
    },
    availabilityLabel() {
      const type = this.otw.availability_type
      const label = AVAILABILITY_LABEL[type] || 'Not specified'
      if (type === 'from_date' && this.otw.availability_date) {
        const formatted = dayjs(this.otw.availability_date).format('DD/MM/YYYY')
        return `${label} ${formatted}`
      }
      return label
    },
  },
}
</script>

<style lang="scss" scoped>
// A global rule (src/assets/scss/base/_animations.scss) zeroes out
// padding-left/right on every .col-12 in the app. b-row still applies its
// usual -15px gutter margin regardless, so with the column's own padding
// gone that -15px isn't cancelled out — it just eats into the card's own
// padding, dragging this text ~15px closer to the card edge than everything
// else in the same card (e.g. the "Files" heading above it, which
// isn't inside a b-row/b-col and isn't affected). Restoring the standard
// 15px column gutter here cancels the row's margin back out, same as it
// would without the global override.
.candidate-profile-detail /deep/ .col-12 {
  padding-left: 15px;
  padding-right: 15px;
}

.candidate-profile-detail__about {
  white-space: pre-line;
}

.candidate-profile-detail__skills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

// Neutral pill, matching the app's gray-100/gray-700 surface tokens rather
// than a semantic color — a skill tag isn't a status.
.candidate-profile-detail__skill-badge {
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 600;
  font-size: 0.8rem;
  padding: 0.4rem 0.85rem;
}

// Each section (Availability / FIFO Preferences / About Me) is just a
// heading plus one or two short lines, but Bootstrap's .mb-5 (3rem/48px,
// !important) is what separates them once the cols stack full-width on
// mobile — on a narrow phone that reads as disconnected, floating blocks
// with far more empty space than the content warrants. Tighten it below the
// md breakpoint (768px, where these cols also start sitting side by side and
// the wider gap earns its keep); desktop is untouched.
@media (max-width: 767.98px) {
  .col-12.mb-5 {
    margin-bottom: 1.75rem !important;
  }
}

.candidate-profile-detail__files {
  margin: 0;
  padding-left: 1.1rem;
}
</style>
